import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Chip, Tooltip, IconButton } from '@mui/material';
import { ChevronRight, ChevronLeft, InfoOutlined } from '@mui/icons-material';
import IntentDisplay from './IntentDisplay';
import IntentEmbeddingTimingBreakdown from './IntentEmbeddingTimingBreakdown';
import JevTargetingBreakdown from './JevTargetingBreakdown';
import TranslationTimingBreakdown from './TranslationTimingBreakdown';
import CacheUsageIndicator from './CacheUsageIndicator';
import { LANGUAGE_ABBREV } from '../config/locales';
import { formatSessionTime } from '../utils/sessionDateTime';
import {
  intentPipelineBadgeForMessage,
  intentEmbeddingTimingUsesIntent30,
} from '../utils/intentPipelineBadge';
import { assistantChipTimings, assistantChipLabelForKey, requestWallClockMs } from '../utils/chatTimings';

function fallbackTooltipLines(message) {
  const fallback = message?.metadata?.fallback;
  if (!fallback || typeof fallback !== 'object') return [];
  const model = typeof fallback.model === 'string' ? fallback.model.trim() : '';
  const kind =
    fallback.kind === 'template' || fallback.kind === 'model'
      ? fallback.kind
      : model
        ? 'model'
        : 'template';
  const lines = ['Primary generation failed; this reply used a fallback.'];
  if (kind === 'model' && model) {
    lines.push(`Fallback model: ${model}`);
  } else {
    lines.push('Fallback: canned reply');
  }
  return lines;
}

function buildTokenUsageTooltipLines(message) {
  const usage = message?.metadata?.tokenUsage;
  const hasUsage = usage && typeof usage === 'object';
  const lines = [];

  if (hasUsage) {
    const totalTokens = Number(usage.totalTokens) || 0;
    const inputTokens = Number(usage.inputTokens) || 0;
    const outputTokens = Number(usage.outputTokens) || 0;
    const totalRequests = Number(usage.totalRequests) || 0;
    const totalCost = Number(usage.totalCost) || 0;

    lines.push(
      'Token usage',
      `Requests: ${totalRequests}`,
      `Tokens: ${totalTokens} (in ${inputTokens}, out ${outputTokens})`,
      `Cost: $${totalCost.toFixed(6)}`
    );

    const byTag = usage.byTag && typeof usage.byTag === 'object' ? usage.byTag : null;
    const topTags = byTag
      ? Object.entries(byTag)
          .sort((a, b) => (Number(b?.[1]?.totalCost) || 0) - (Number(a?.[1]?.totalCost) || 0))
          .slice(0, 6)
      : [];

    if (topTags.length > 0) {
      lines.push('', 'By tag:');
      topTags.forEach(([tag, entry]) => {
        const tagTokens = Number(entry?.totalTokens) || 0;
        const tagCost = Number(entry?.totalCost) || 0;
        const modelFromEntry = typeof entry?.model === 'string' && entry.model.trim().length > 0
          ? entry.model.trim()
          : null;
        const modelsFromEntry = Array.isArray(entry?.models)
          ? entry.models.filter((m) => typeof m === 'string' && m.trim().length > 0).map((m) => m.trim())
          : [];
        const modelLabel = modelFromEntry || (modelsFromEntry.length > 0 ? modelsFromEntry.join(', ') : 'unknown');
        lines.push(`- ${tag} (${modelLabel}): ${tagTokens} tokens, $${tagCost.toFixed(6)}`);
      });
    }
  }

  const fallbackLines = fallbackTooltipLines(message);
  if (fallbackLines.length > 0) {
    if (lines.length > 0) lines.push('');
    lines.push(...fallbackLines);
  }

  return lines.length > 0 ? lines.join('\n') : null;
}

function hidePatientLlmChipBecauseIntent20Embedding(timings) {
  const ms = timings?.intentEmbeddingTiming?.patientPrompt?.duration_ms;
  return typeof ms === 'number' && !Number.isNaN(ms) && ms > 0;
}

function hideMessageGenerationBecauseIntent20PatientInEmbedding(timings) {
  const embMs = timings?.intentEmbeddingTiming?.patientPrompt?.duration_ms;
  const topPatient = timings?.patientLlmMs;
  return (
    typeof embMs === 'number' &&
    !Number.isNaN(embMs) &&
    embMs > 0 &&
    typeof topPatient === 'number' &&
    !Number.isNaN(topPatient) &&
    topPatient === embMs &&
    typeof timings?.messageGeneration === 'number' &&
    timings.messageGeneration > 0
  );
}

function jevTargetingDetail(timings) {
  const detail = timings?.jevTargeting;
  return detail && typeof detail === 'object' ? detail : null;
}

/** Jev targeting runs beside embedding inside the Intent phase: shown on the Target chip, not added to the total. */
function jevTargetChipMs(timings) {
  if (Number(timings?.targetIdentification) > 0) return undefined;
  const ms = jevTargetingDetail(timings)?.duration_ms;
  return typeof ms === 'number' && !Number.isNaN(ms) ? Math.max(1, ms) : undefined;
}

function timingValueForAssistantChips(timings, key) {
  if (!timings) return undefined;
  if (key === 'targetIdentification' && jevTargetChipMs(timings) != null) return jevTargetChipMs(timings);
  if (key === 'patientLlmMs' && hidePatientLlmChipBecauseIntent20Embedding(timings)) return undefined;
  if (key === 'messageGeneration' && hideMessageGenerationBecauseIntent20PatientInEmbedding(timings)) return undefined;
  const v = timings[key];
  return typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
}

/** Pill timing chips — half-height radius, neutral grey (aligned with admin session history). */
const TIMING_CHIP_H = 22;
const timingChipSx = {
  height: TIMING_CHIP_H,
  maxHeight: TIMING_CHIP_H,
  borderRadius: `${TIMING_CHIP_H / 2}px`,
  fontSize: '0.65rem',
  fontWeight: 500,
  '& .MuiChip-label': {
    px: 0.75,
    py: 0,
  },
};

const TIMING_FIELDS = [
  { key: 'translation', label: 'Translation', includeInTotal: true },
  { key: 'targetIdentification', label: 'Target', includeInTotal: true },
  { key: 'intentIdentification', label: 'Intent', includeInTotal: true },
  { key: 'messageGeneration', label: 'Generation', includeInTotal: true },
  // Supplemental metric (not part of top-level phase total; usually overlaps with other phases).
  { key: 'patientLlmMs', label: 'Patient LLM', includeInTotal: false }
];

const ChatMessageBubble = ({
  message,
  alignRight = false,
  backgroundColor = '#F5F5F5',
  textColor = '#424242',
  senderName = '',
  senderIcon = null,
  onClick,
  clickable = false,
  showTimings = false,
  showTimingDetails = false,
  showLanguage = true,
  showTokenUsageTooltip = false,
  showIntents = false,
  intentProps = {},
  actions = null,
  footer = null,
  children = null,
  messageVariant = 'body1',
  sx = {}
}) => {
  /** Which timing chip detail is open: 'translation' | 'intent' | 'target' | null */
  const [openTimingDetail, setOpenTimingDetail] = useState(null);
  const [timingCategoriesExpanded, setTimingCategoriesExpanded] = useState(false);
  const [englishExpanded, setEnglishExpanded] = useState(false);
  const rawTimingsPayload = message?.timings ?? message?.metadata?.timings;
  const timingsPayload = assistantChipTimings(rawTimingsPayload);
  const intentEmbeddingTiming = timingsPayload?.intentEmbeddingTiming;
  const translationDetail = timingsPayload?.translationDetail;
  const jevTargeting = jevTargetingDetail(timingsPayload);
  const intentPipelineBadge = intentPipelineBadgeForMessage(message);
  const usesIntent30 = intentEmbeddingTimingUsesIntent30(intentEmbeddingTiming) || Boolean(jevTargeting);
  const timingDisplayFields =
    timingsPayload
      ? TIMING_FIELDS.filter(({ key }) => {
          const v = timingValueForAssistantChips(timingsPayload, key);
          return typeof v === 'number' && v > 0;
        })
      : [];
  const timingChipsSumMs =
    timingsPayload &&
    TIMING_FIELDS.reduce((sum, { key, includeInTotal }) => {
      if (!includeInTotal) return sum;
      const v = timingValueForAssistantChips(timingsPayload, key);
      return sum + (typeof v === 'number' && v > 0 ? v : 0);
    }, 0);
  const timingTotalMs =
    requestWallClockMs(rawTimingsPayload ?? timingsPayload) ??
    (timingChipsSumMs > 0 ? timingChipsSumMs : 0);
  const hasTimings = Boolean(
    showTimings &&
      timingsPayload &&
      (showTimingDetails ? timingDisplayFields.length > 0 : timingTotalMs > 0)
  );
  const hasLanguage = Boolean(showLanguage && message?.language && LANGUAGE_ABBREV[message.language]);
  const senderIsClient = (message?.sender || '').toLowerCase() === 'client';
  const hasTopFooterRow = Boolean(hasTimings || hasLanguage || actions);
  const hasFooterSection = Boolean(hasTopFooterRow || footer);
  const tokenUsageTooltip =
    showTokenUsageTooltip && !senderIsClient ? buildTokenUsageTooltipLines(message) : null;
  const rawMsg = typeof message?.message === 'string' ? message.message : JSON.stringify(message?.message);
  const hasEnglish =
    senderIsClient &&
    typeof message?.english === 'string' &&
    message.english.length > 0 &&
    message.english !== rawMsg;

  useEffect(() => {
    setEnglishExpanded(false);
    setTimingCategoriesExpanded(false);
    setOpenTimingDetail(null);
  }, [message?.id]);

  useEffect(() => {
    if (!showTimingDetails) {
      setTimingCategoriesExpanded(false);
      setOpenTimingDetail(null);
    }
  }, [showTimingDetails]);

  const bubble = (
    <Paper
        elevation={1}
        onClick={onClick}
        sx={{
          p: 2,
          backgroundColor,
          ml: 0,
          mr: 0,
          width: { xs: '90%', sm: '86%', md: '82%' },
          maxWidth: { xs: '90%', sm: '86%', md: '82%' },
          minWidth: 0,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          cursor: clickable ? 'pointer' : 'default',
          transition: 'box-shadow 0.2s ease-in-out',
          '&:hover': clickable ? { boxShadow: 2 } : {},
          ...sx
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {senderIcon}
          <Typography variant="subtitle2" sx={{ ml: senderIcon ? 1 : 0, fontWeight: 'bold', color: textColor }}>
            {senderName}
          </Typography>
          {intentPipelineBadge ? (
            <Tooltip title={intentPipelineBadge.tooltip} arrow placement="top">
              <Chip label={intentPipelineBadge.label} size="small" sx={intentPipelineBadge.sx} />
            </Tooltip>
          ) : null}
          <CacheUsageIndicator message={message} origin="client" />
          {tokenUsageTooltip && (
            <Tooltip
              title={
                <Typography
                  component="span"
                  sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem', color: 'common.white' }}
                >
                  {tokenUsageTooltip}
                </Typography>
              }
              arrow
              placement="top"
            >
              <InfoOutlined
                sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', ml: 0.5 }}
                aria-label="Token usage details"
              />
            </Tooltip>
          )}
          <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
            {message?.timestamp ? formatSessionTime(message.timestamp) : ''}
          </Typography>
        </Box>

        <Typography variant={messageVariant} sx={{ color: textColor }}>
          {typeof message?.message === 'string' ? message.message : JSON.stringify(message?.message)}
        </Typography>

        {hasEnglish && (
          <Box
            sx={{
              overflow: 'hidden',
              maxHeight: englishExpanded ? '500px' : '0px',
              opacity: englishExpanded ? 1 : 0,
              transition: 'max-height 0.3s ease-in-out, opacity 0.2s ease-in-out',
              mt: englishExpanded ? 1.5 : 0
            }}
          >
            <Box
              sx={{
                p: 1.5,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                borderRadius: 1,
                borderLeft: '3px solid',
                borderColor: textColor
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                English:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                {message.english}
              </Typography>
            </Box>
          </Box>
        )}
        {hasEnglish && (
          <Typography
            variant="caption"
            onClick={(e) => {
              e.stopPropagation();
              setEnglishExpanded((v) => !v);
            }}
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
              display: 'block',
              mt: 0.5,
              fontSize: '0.7rem',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            {englishExpanded ? 'Hide English' : 'Show English'}
          </Typography>
        )}

        {children}

        {showIntents && (
          <IntentDisplay
            intents={intentProps.intents}
            softwareIntents={intentProps.softwareIntents}
            surgeryIntents={intentProps.surgeryIntents}
            patientSelectIntents={intentProps.patientSelectIntents}
            labResultsIntents={intentProps.labResultsIntents}
            evalReportIntents={intentProps.evalReportIntents}
            legacyIntent={intentProps.legacyIntent}
            softwareIntent={intentProps.softwareIntent}
            patientSelect={intentProps.patientSelect}
            dicomMessage={intentProps.dicomMessage}
          />
        )}

        {hasFooterSection && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            {hasTopFooterRow && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: 0 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    {hasTimings && !showTimingDetails && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={timingTotalMs > 0 ? `Total: ${timingTotalMs}ms` : 'Timing details'}
                        sx={{ ...timingChipSx, cursor: 'default' }}
                      />
                    )}
                    {hasTimings && showTimingDetails && !timingCategoriesExpanded && (
                      <Tooltip title="Expand timing breakdown" arrow placement="top">
                        <Chip
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            setTimingCategoriesExpanded(true);
                          }}
                          aria-label="Expand timing breakdown"
                          label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                              <span>{timingTotalMs > 0 ? `Total: ${timingTotalMs}ms` : 'Timing details'}</span>
                              <ChevronRight sx={{ fontSize: 18, ml: 0.25 }} aria-hidden />
                            </Box>
                          }
                          sx={{ ...timingChipSx, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    )}
                    {hasTimings &&
                      showTimingDetails &&
                      timingCategoriesExpanded &&
                      timingDisplayFields.map(({ key }) => {
                        const label = assistantChipLabelForKey(key, rawTimingsPayload ?? timingsPayload);
                        const ms = timingValueForAssistantChips(timingsPayload, key);
                        if (ms == null || ms <= 0) return null;
                        const detailKey =
                          key === 'translation' && translationDetail?.steps?.length
                            ? 'translation'
                            : key === 'intentIdentification' && intentEmbeddingTiming
                              ? 'intent'
                              : key === 'targetIdentification' && jevTargeting
                                ? 'target'
                                : null;
                        const expandable = detailKey != null;
                        const isOpen = expandable && openTimingDetail === detailKey;
                        const dimOthers = openTimingDetail != null && !isOpen;
                        const timingChip = (
                          <Chip
                            label={`${label}: ${ms}ms`}
                            size="small"
                            variant={isOpen ? 'filled' : 'outlined'}
                            onClick={
                              expandable
                                ? (e) => {
                                    e.stopPropagation();
                                    setOpenTimingDetail((v) => (v === detailKey ? null : detailKey));
                                  }
                                : undefined
                            }
                            sx={{
                              ...timingChipSx,
                              opacity: dimOthers ? 0.38 : 1,
                              cursor: expandable ? 'pointer' : 'default',
                              transition: 'opacity 0.2s ease',
                            }}
                          />
                        );
                        if (expandable) {
                          return (
                            <Tooltip
                              key={key}
                              title={
                                detailKey === 'translation'
                                  ? `${isOpen ? 'Hide' : 'Show'} translation details`
                                  : detailKey === 'target'
                                    ? `${isOpen ? 'Hide' : 'Show'} Intent 3.0 targeting details`
                                    : `${isOpen ? 'Hide' : 'Show'} Intent ${usesIntent30 ? '3.0' : '2.0'} timing breakdown`
                              }
                              arrow
                              placement="top"
                            >
                              {timingChip}
                            </Tooltip>
                          );
                        }
                        return (
                          <Tooltip key={key} title={label} arrow placement="top">
                            {timingChip}
                          </Tooltip>
                        );
                      })}
                    {hasTimings && showTimingDetails && timingCategoriesExpanded && (
                      <IconButton
                        size="small"
                        aria-label="Collapse timing breakdown"
                        onClick={(e) => {
                          e.stopPropagation();
                          setTimingCategoriesExpanded(false);
                        }}
                        sx={{ p: 0.25, ml: 0.25 }}
                      >
                        <ChevronLeft sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>
                  {showTimingDetails &&
                  timingCategoriesExpanded &&
                  openTimingDetail === 'translation' &&
                  translationDetail ? (
                    <TranslationTimingBreakdown detail={translationDetail} />
                  ) : null}
                  {showTimingDetails &&
                  timingCategoriesExpanded &&
                  openTimingDetail === 'intent' &&
                  intentEmbeddingTiming ? (
                    <IntentEmbeddingTimingBreakdown
                      detail={intentEmbeddingTiming}
                      requestDurationMs={requestWallClockMs(rawTimingsPayload ?? timingsPayload)}
                    />
                  ) : null}
                  {showTimingDetails &&
                  timingCategoriesExpanded &&
                  openTimingDetail === 'target' &&
                  jevTargeting ? (
                    <JevTargetingBreakdown detail={jevTargeting} />
                  ) : null}
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexShrink: 0, ml: 1 }}>
                  {hasLanguage && (
                    <Tooltip title={message.language} arrow placement="top">
                      <Chip
                        label={LANGUAGE_ABBREV[message.language]}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.65rem', fontWeight: 600, cursor: 'default' }}
                      />
                    </Tooltip>
                  )}
                  {actions}
                </Box>
              </Box>
            )}
            {footer}
          </Box>
        )}
      </Paper>
  );

  return (
    <Box sx={{ mb: 2, display: 'flex', justifyContent: alignRight ? 'flex-end' : 'flex-start' }}>
      {bubble}
    </Box>
  );
};

export default ChatMessageBubble;
